import { useEffect, useState } from 'react'
import { Search, Store, Shield, Users, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { brandService, Brand } from '../services/brandService'
import { productService, Product } from '../services/productService'
import { showcaseService } from '../services/showcaseService'

export default function Home() {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [activeTab, setActiveTab] = useState<'brands' | 'products' | 'halls'>('brands')
  interface HallCard { _id: string; name: string; visitCount?: number; exhibitCount?: number }
  const [halls, setHalls] = useState<HallCard[]>([])
  const [brands, setBrands] = useState<Brand[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const [brandResult, productResult, hallResult] = await Promise.all([
          brandService.getBrands(1, 20),
          productService.getProducts({ page: 1, limit: 20 }),
          showcaseService.getPublicSpaces(undefined, 6).catch(() => ({ items: [], nextCursor: null })),
        ])
        setBrands(brandResult.items)
        setProducts(productResult.items)
        setHalls((hallResult.items ?? []) as HallCard[])
      } catch (e) {
        setError((e as Error).message || '加载失败')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const filteredBrands = brands.filter((b) =>
    b.name.toLowerCase().includes(searchKeyword.toLowerCase()),
  )
  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchKeyword.toLowerCase()),
  )

  return (
    <div className="space-y-6 py-4">
      {/* Hero */}
      <div className="cyber-card rounded-2xl p-6 corner-accent">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-cyber-neon" />
          <span className="text-cyber-neon font-cyber text-sm tracking-wider">正品认证 · 担保交易</span>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          源头认证让正品可被证明，担保交易让粉丝敢付款
        </p>
        <Link
          to="/verify"
          className="cyber-btn inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium"
        >
          <Search className="w-4 h-4" />
          立即验真
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Shield, label: '已验证', value: '—', color: 'text-cyber-yellow' },
          { icon: Store, label: '品牌方', value: brands.length ? String(brands.length) : '—', color: 'text-cyber-neon' },
          { icon: Users, label: '商品', value: products.length ? String(products.length) : '—', color: 'text-cyber-pink' },
        ].map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className="cyber-card rounded-xl p-3 text-center">
              <Icon className={`w-5 h-5 ${stat.color} mx-auto mb-1`} />
              <div className={`text-lg font-cyber font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-[10px] text-gray-500">{stat.label}</div>
            </div>
          )
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="搜索品牌或商品..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-cyber-dark border border-cyber-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyber-neon"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'brands', label: '品牌' },
          { id: 'products', label: '商品' },
          { id: 'halls', label: '展馆' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as 'brands' | 'products' | 'halls')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-cyber-neon/20 text-cyber-neon border border-cyber-neon/50'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center text-gray-500 py-10 text-sm">加载中...</div>}
      {error && <div className="text-center text-red-400 py-10 text-sm">{error}</div>}

      {/* Brand Grid */}
      {!loading && !error && activeTab === 'brands' && (
        filteredBrands.length === 0 ? (
          <div className="text-center text-gray-600 py-10 text-sm border border-dashed border-cyber-border rounded-xl">
            暂无入驻品牌
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredBrands.map((brand) => (
              <Link
                key={brand._id}
                to={`/shop/${brand._id}`}
                className="cyber-card rounded-xl p-4 hover:shadow-lg hover:shadow-cyber-neon/10 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyber-neon/20 to-cyber-pink/20 border border-cyber-border flex items-center justify-center text-2xl overflow-hidden">
                    {brand.logo ? <img src={brand.logo} alt="" className="w-full h-full object-cover" /> : '🏪'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-white truncate">{brand.name}</span>
                      <Shield className="w-3 h-3 text-cyber-neon shrink-0" />
                    </div>
                    <div className="text-xs text-gray-500 truncate">{brand.description || '正品认证品牌'}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>源头认证</span>
                  <span className="text-cyber-neon">进店 →</span>
                </div>
              </Link>
            ))}
          </div>
        )
      )}

      {/* Product Grid */}
      {!loading && !error && activeTab === 'products' && (
        filteredProducts.length === 0 ? (
          <div className="text-center text-gray-600 py-10 text-sm border border-dashed border-cyber-border rounded-xl">
            暂无在售商品
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredProducts.map((product) => (
              <Link
                key={product._id}
                to={`/product/${product._id}`}
                className="cyber-card rounded-xl overflow-hidden group"
              >
                <div className="aspect-square bg-cyber-dark relative overflow-hidden">
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-lg bg-cyber-panel border border-cyber-border flex items-center justify-center">
                        <span className="text-2xl">🎴</span>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 px-2 py-1 rounded-md text-[10px] font-bold bg-gradient-to-r from-cyber-neon to-cyber-pink text-white">
                    已认证
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium truncate">{product.name}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-cyber-yellow font-cyber font-bold">
                      ¥{(product.price / 100).toFixed(2)}
                    </span>
                    <span className="w-8 h-8 rounded-lg bg-cyber-neon/20 border border-cyber-neon/50 flex items-center justify-center text-cyber-neon">
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )
      )}

      {/* 展馆 tab */}
      {!loading && !error && activeTab === 'halls' && (
        halls.length === 0 ? (
          <div className="text-center text-gray-600 py-10 text-sm border border-dashed border-cyber-border rounded-xl">
            还没有公开的展馆
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {halls.map((h) => (
              <Link key={h._id} to={`/space/${h._id}`}
                className="cyber-card rounded-xl p-4 hover:border-cyber-neon/50 transition-colors">
                <div className="text-2xl mb-2">🏛️</div>
                <div className="text-sm font-medium text-white truncate">{h.name}</div>
                <div className="text-xs text-gray-500 mt-1">{h.exhibitCount ?? 0} 件展品 · {h.visitCount ?? 0} 访问</div>
              </Link>
            ))}
          </div>
        )
      )}
    </div>
  )
}
